var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { set, groupBy } from 'lodash';
import createRecords from 'src/utils/entityOperations/createRecords';
import { encrypt } from 'src/utils/encryption/encrypt';
import tryWithFullscreenLoading from 'src/utils/tryWithFullscreenLoading';
function digestUploadListFile(e, ctx) {
    return __awaiter(this, void 0, void 0, function* () {
        // starting parsing file into variable 'input' as array of objects
        const input = [];
        const rows = String(e.target.result).split('\n');
        const fields = rows[0].split(',').map((r) => r.trim());
        for (let i = 1; i <= rows.length - 1; i++) {
            if (rows[i] === '')
                continue;
            const values = rows[i].split(',').map((r) => r.trim());
            const item = {};
            for (let j = 0; j < fields.length; j++) {
                set(item, fields[j], values[j]); // set is used here for visual convenience only
            }
            input.push(item);
        }
        // starting validating
        const errors = [];
        // validating if column names are known
        for (const field of fields) {
            if (!ctx.columnsLocalCopy.map((r) => r.field).includes(field)) {
                errors.push(`Column name "${field}" is not a valid field name for this table`);
            }
        }
        // validating if required columns are present at all
        const requiredFields = ctx.columnsLocalCopy.filter((r) => r.required).map((r) => r.field);
        for (const reqCol of requiredFields) {
            if (!fields.includes(reqCol)) {
                errors.push(`Column "${reqCol}" is required for this table, but is not found found in the csv file`);
            }
        }
        // validating referenced values consistency
        const referencingColumns = ctx.columnsLocalCopy.filter((r) => r.references);
        for (const refCol of referencingColumns) {
            // Use below line to get possible values of referencing fields - this is needed in excel template
            // console.log(refCol.field + '=' + refCol.references.map((r: any) => r.name).join(','))
            if (fields.includes(refCol.field || '')) {
                if (!refCol.references)
                    return;
                const referenced = refCol.references.map((r) => { return { id: r.id, name: r.name }; });
                if (referenced === undefined)
                    return;
                const referencedIds = referenced.map((r) => r.id);
                const referencedNames = referenced.map((r) => r.name);
                const presentValues = input.map((r) => r[refCol.field || '']);
                for (const pv of presentValues) {
                    if (!referencedIds.includes(Number(pv)) && !referencedNames.includes(pv)) {
                        errors.push(`Value "${pv}" in field "${refCol.field}" is not valid`);
                    }
                    else if (referencedNames.includes(pv) && !referencedIds.includes(Number(pv))) {
                        // substituting name with referenced id, in place
                        const replaceValue = (referenced.find((r) => r.name === pv) || {}).id;
                        if (!replaceValue)
                            return;
                        input.forEach((r) => {
                            if (r[refCol.field] === pv)
                                r[refCol.field] = replaceValue;
                        });
                    }
                }
            }
        }
        const duplicateCheckerFunc = (space, errorMsgFunc) => {
            // function for checking unique constraints
            for (const constraint of ctx.uniqueConstraints) {
                const a = space.map((r) => {
                    const res = {};
                    for (const field of constraint)
                        res[field] = String(r[field] || '');
                    return res;
                });
                const b = groupBy(a, (r) => JSON.stringify(r));
                for (const d in b) {
                    const len = b[d].length;
                    if (len > 1)
                        errors.push(errorMsgFunc(len, d));
                }
            }
        };
        if (ctx.overwriteOnUniqueConstraintConflict) {
            // checking unique constraints, within the file
            duplicateCheckerFunc(input, (len, d) => `${len} duplicate values within your file: ${d}`);
        }
        else {
            // checking unique constraints, between the file and existing records
            duplicateCheckerFunc([...input, ...ctx.enrichedSourceData], (len, d) => `${len} duplicate values between your file and existing records: ${d}`);
        }
        // fixing empty values, substituting '' with NULL
        for (const row of input) {
            for (const field of fields) {
                if (row[field] === '')
                    row[field] = null;
            }
        }
        // validating rows
        for (const [index, row] of input.entries()) {
            for (const rule of ctx.rowValidationRules) {
                if (!rule.isValid(row)) {
                    errors.push(`Line ${index}: ${rule.errorMessage}`);
                }
            }
        }
        if (errors.length > 0) {
            ctx.$q.notify({ message: '<div class="text-h6">Errors in the CSV file</div><ol>' + errors.map((r) => '<li>' + r + '</li>').join('') + '<ol>', html: true, timeout: 10000 });
        }
        else {
            // encrypting before sending.  This is done last to avoid issues in validation phase
            for (const row of input) {
                for (const field of fields) {
                    const foundColumn = ctx.columnsLocalCopy.find((col) => col.field === field);
                    if (foundColumn && foundColumn.encrypted)
                        row[field] = String(encrypt(String(row[field])));
                }
            }
            let pendingRequests = input.length;
            input.forEach((row) => __awaiter(this, void 0, void 0, function* () {
                const response = yield createRecords(ctx.localEntityName, [Object.assign(Object.assign({}, row), { do_overwrite_on_conflict: true })]);
                if (response)
                    pendingRequests -= 1;
            }));
            // keep checking if requests still pending
            const checkIfSendingCompleted = () => {
                if (pendingRequests === 0) {
                    ctx.callRefFunc(ctx, 'uploadListDialogue', 'hide');
                }
                else {
                    setTimeout(checkIfSendingCompleted, 1000);
                }
            };
            checkIfSendingCompleted();
        }
        return true;
    });
}
export default function (ctx) {
    return __awaiter(this, void 0, void 0, function* () {
        tryWithFullscreenLoading(() => __awaiter(this, void 0, void 0, function* () {
            const reader = new FileReader();
            reader.onloadend = (e) => __awaiter(this, void 0, void 0, function* () { return digestUploadListFile(e, ctx); });
            reader.readAsText(ctx.uploadListFile);
        }), 'Uploading data...');
    });
}
